import { Directive, OnDestroy, OnInit, booleanAttribute, inject, input } from '@angular/core';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Dialog } from 'primeng/dialog';
import { OverlayPanel } from 'primeng/overlaypanel';
import { Subscription } from 'rxjs';
import { HisRestoreFocusOrchestratorService } from '../service/base/his-restore-focus-orchestrator.service';

/**
 * Directive that globally restores focus to the element that triggered an overlay when the overlay is closed.
 * This is useful for maintaining accessibility and user experience by returning focus to the appropriate element.
 * 
 * It does not rely on specific trigger IDs or classes, but instead listens to the `onShow` and `onHide` events
 * of the overlay components to determine when to restore focus.
 * 
 * This directive is implicitly applied to all `p-dialog`, `p-confirmDialog`, and `p-overlayPanel` components.
 * It can be disabled by adding the `disableGlobalRestoreFocus` attribute on the overlay component.
 */
@Directive( {
    // eslint-disable-next-line @angular-eslint/directive-selector
    selector: 'p-dialog, p-confirmDialog, p-overlayPanel',
    standalone: false
} )
export class HisGlobalRestoreFocusDirective implements OnInit, OnDestroy {
    private readonly orchestrator = inject( HisRestoreFocusOrchestratorService );

    /** Flag to disable the global restore focus behavior */
    public readonly disabled = input( false, { alias: 'disableGlobalRestoreFocus', transform: booleanAttribute } );

    // `self`: ohne die Beschraenkung wuerde ein verschachteltes Overlay den umschliessenden Dialog
    // injizieren und dessen Fokus-Wiederherstellung ausloesen, sobald es zerstoert wird.
    private readonly dialog = inject( Dialog, { optional: true, self: true } );
    private readonly confirmDialog = inject( ConfirmDialog, { optional: true, self: true } );
    private readonly overlayPanel = inject( OverlayPanel, { optional: true, self: true } );

    private readonly subscriptions = new Subscription();
    private overlayInstance?: object;

    ngOnInit(): void {
        if ( this.disabled() ) {
            return;
        }

        const overlayInstance = this.getOverlayInstance();
        if ( !overlayInstance ) {
            return;
        }
        this.overlayInstance = overlayInstance;

        const showStream = ( overlayInstance as any ).onShow;
        const hideStream = ( overlayInstance as any ).onHide;

        if ( showStream?.subscribe ) {
            this.subscriptions.add(
                showStream.subscribe( ( event: any ) => {
                    this.orchestrator.registerOpen( overlayInstance, this.extractTriggerElement( event ) );
                } )
            );
        }

        if ( hideStream?.subscribe ) {
            this.subscriptions.add(
                hideStream.subscribe( () => {
                    this.orchestrator.registerClose( overlayInstance );
                } )
            );
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        if ( this.overlayInstance ) {
            this.orchestrator.registerClose( this.overlayInstance );
        }
    }

    private getOverlayInstance(): object | null {
        return this.dialog ?? this.confirmDialog ?? this.overlayPanel ?? null;
    }

    private extractTriggerElement( event: any ): HTMLElement | null {
        const target = event?.target
            ?? event?.currentTarget
            ?? event?.originalEvent?.target
            ?? event?.srcEvent?.target
            ?? null;

        return target instanceof HTMLElement ? target : null;
    }
}
